import { ExportFrame } from './export-frame.js';
import { InspectionHandlerLoader } from './inspection-handler-loader.js';
import { trackEvent } from './analytics.js';

export class MapExportControl {
    constructor() {
        this._map = null;
        this._container = null;
        this._iframe = null;
        this._frame = null;
        this._isExporting = false;
        this._exportCancelled = false;
        this._title = '';
        this._description = '';
        this._titleCustomized = false;
        this._descriptionCustomized = false;
        this._moveendHandler = null;
        this._footerTemplateCache = null;
        this._exportSettings = null;
        this._isPanelOpen = false;
    }

    onAdd(map) {
        this._map = map;
        this._container = document.createElement('div');
        this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';

        const button = document.createElement('button');
        button.className = 'mapboxgl-ctrl-icon mapboxgl-ctrl-export';
        button.type = 'button';
        button.title = 'Export Map';

        const icon = document.createElement('sl-icon');
        icon.name = 'download';
        icon.style.fontSize = '18px';
        button.appendChild(icon);

        button.addEventListener('click', () => this._toggle());
        this._container.appendChild(button);

        this._frame = new ExportFrame(map, this);
        this._createIframe();

        this._moveendHandler = () => {
            if (this._isPanelOpen && !this._isExporting && !this._titleCustomized) {
                this._updateTitleFromLocation();
            }
        };
        map.on('moveend', this._moveendHandler);

        setTimeout(() => {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has('export')) {
                this._show();
            }
        }, 500);

        return this._container;
    }

    onRemove() {
        if (this._map && this._moveendHandler) {
            this._map.off('moveend', this._moveendHandler);
            this._moveendHandler = null;
        }
        if (this._frame) {
            this._frame.remove();
        }
        if (this._processingOverlay && this._processingOverlay.parentNode) {
            this._processingOverlay.parentNode.removeChild(this._processingOverlay);
        }
        if (this._iframe && this._iframe.parentNode) {
            this._iframe.parentNode.removeChild(this._iframe);
        }
        this._container.parentNode.removeChild(this._container);
        this._map = null;
    }

    _onFrameChange(aspectRatio) {
        if (this._iframe && this._iframe.contentWindow) {
            this._iframe.contentWindow.postMessage({
                type: 'frame-aspect-changed',
                aspectRatio: aspectRatio
            }, '*');
        }
    }

    _onFrameInteractionStart() {
        if (this._iframe) {
            this._iframe.style.opacity = '0.4';
        }
    }

    _createIframe() {
        // Iframe element only; src is deferred so map-export.html and its
        // bundle don't load on the critical path. preload() loads it once
        // the map is idle; _show() loads it if the user opens the panel first.
        this._iframe = document.createElement('iframe');
        this._iframe.className = 'map-export-iframe';
        this._iframeSrcLoaded = false;

        const isMobile = window.innerWidth <= 768;
        const panelWidth = isMobile ? '100%' : '400px';
        const panelRight = isMobile ? '0' : '8px';
        const panelTop = '52px';
        const panelHeight = isMobile ? '60vh' : '85vh';

        this._iframe.style.cssText = `
            position: fixed;
            top: ${panelTop};
            right: ${panelRight};
            width: ${panelWidth};
            max-width: calc(100vw - 70px);
            height: ${panelHeight};
            max-height: 85vh;
            border: none;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            z-index: 1000;
            display: none;
            transition: opacity 0.2s ease;
            background: #1e293b;
            overflow: hidden;
        `;
        document.body.appendChild(this._iframe);

        this._iframe.addEventListener('mouseenter', () => {
            this._iframe.style.opacity = '1';
        });

        this._processingOverlay = document.createElement('div');
        this._processingOverlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.7);
            backdrop-filter: blur(2px);
            z-index: 999;
            display: none;
        `;
        document.body.appendChild(this._processingOverlay);

        window.addEventListener('message', (event) => {
            const { type, config } = event.data;

            if (type === 'toggle-export') {
                this._toggle();
                return;
            }

            if (event.source !== this._iframe.contentWindow) return;

            if (type === 'export-ready') {
                if (this._isPanelOpen) this._updateTitleFromLocation();
            } else if (type === 'export-close') {
                this._hide();
            } else if (type === 'export-start') {
                this._handleExport(config);
            } else if (type === 'title-changed') {
                this._title = config.title;
                this._titleCustomized = config.customized;
            } else if (type === 'description-changed') {
                this._description = config.description;
                this._descriptionCustomized = config.customized;
            } else if (type === 'frame-show') {
                if (this._iframe.style.display !== 'none') {
                    this._frame.show();
                    this._frame.setAspectRatio(config.aspectRatio || 1.414);
                }
            } else if (type === 'frame-hide') {
                this._frame.hide();
            } else if (type === 'frame-aspect') {
                if (this._iframe.style.display !== 'none') {
                    this._frame.setAspectRatio(config.aspectRatio);
                }
            } else if (type === 'show-qr-fullscreen') {
                this._showQRFullscreen(event.data.url);
            } else if (type === 'export-cancel') {
                this._exportCancelled = true;
            } else if (type === 'processing-overlay-show') {
                if (this._processingOverlay) {
                    this._processingOverlay.style.display = 'block';
                    this._iframe.style.zIndex = '1001';
                }
            } else if (type === 'processing-overlay-hide') {
                if (this._processingOverlay) {
                    this._processingOverlay.style.display = 'none';
                    this._iframe.style.zIndex = '1000';
                }
            } else if (type === 'request-selected-features') {
                const selectedFeatures = this._getSelectedFeatures();
                const bounds = this._map.getBounds();

                const featuresWithViewFlag = selectedFeatures.map(item => ({
                    ...item,
                    isInView: this._isFeatureCompletelyInView(item.feature, bounds)
                }));

                this._iframe.contentWindow.postMessage({
                    type: 'selected-features',
                    features: featuresWithViewFlag
                }, '*');
            } else if (type === 'export-settings-changed') {
                this._exportSettings = event.data.settings;
                if (this._isPanelOpen && window.urlManager) {
                    window.urlManager.updateExportParam(this._exportSettings);
                }
            }
        });
    }

    _showQRFullscreen(url) {
        const overlay = document.createElement('div');
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.95);
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            z-index: 10000;
            padding: 20px;
        `;

        const closeBtn = document.createElement('button');
        closeBtn.textContent = '×';
        closeBtn.style.cssText = `
            position: absolute;
            top: 20px;
            right: 20px;
            width: 48px;
            height: 48px;
            background: rgba(255, 255, 255, 0.1);
            border: 2px solid rgba(255, 255, 255, 0.3);
            border-radius: 50%;
            color: white;
            font-size: 32px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s;
            z-index: 10001;
            line-height: 1;
            padding: 0;
        `;

        const content = document.createElement('div');
        content.style.cssText = `
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
            z-index: 10001;
            pointer-events: none;
        `;

        const qrCode = document.createElement('sl-qr-code');
        qrCode.value = url;
        qrCode.size = 400;
        qrCode.style.cssText = `
            max-width: 90vw;
            max-height: 70vh;
        `;

        const urlText = document.createElement('div');
        urlText.textContent = url;
        urlText.style.cssText = `
            color: white;
            font-size: 14px;
            text-align: center;
            word-break: break-all;
            padding: 0 20px;
            max-width: 90vw;
            font-family: monospace;
        `;

        const closeOverlay = () => {
            if (overlay.parentNode) {
                overlay.parentNode.removeChild(overlay);
            }
        };

        closeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            closeOverlay();
        });

        closeBtn.addEventListener('mouseenter', () => {
            closeBtn.style.background = 'rgba(255, 255, 255, 0.2)';
            closeBtn.style.borderColor = 'rgba(255, 255, 255, 0.5)';
        });

        closeBtn.addEventListener('mouseleave', () => {
            closeBtn.style.background = 'rgba(255, 255, 255, 0.1)';
            closeBtn.style.borderColor = 'rgba(255, 255, 255, 0.3)';
        });

        overlay.addEventListener('click', (e) => {
            if (e.target === overlay) {
                closeOverlay();
            }
        });

        content.appendChild(qrCode);
        content.appendChild(urlText);
        overlay.appendChild(closeBtn);
        overlay.appendChild(content);
        document.body.appendChild(overlay);
    }

    _toggle() {
        if (this._iframe.style.display === 'none') {
            this._show();
        } else {
            this._hide();
        }
    }

    preload() {
        if (this._iframeSrcLoaded || !this._iframe) return;
        this._iframe.src = 'map-export.html';
        this._iframeSrcLoaded = true;
    }

    _show() {
        this.preload();
        this._iframe.style.display = 'block';
        this._isPanelOpen = true;

        const urlSettings = this._parseExportURL();
        if (urlSettings) {
            this._exportSettings = urlSettings;
        }

        if (this._exportSettings && window.urlManager) {
            window.urlManager.updateExportParam(this._exportSettings);
        }

        setTimeout(() => {
            if (this._iframe && this._iframe.contentWindow) {
                this._iframe.contentWindow.postMessage({
                    type: 'export-opened',
                    initialSettings: this._exportSettings
                }, '*');
            }
        }, 50);
    }

    _hide() {
        this._iframe.style.display = 'none';
        this._frame.hide();
        this._isPanelOpen = false;

        if (window.urlManager) {
            window.urlManager.updateExportParam(null);
        }

        if (this._iframe && this._iframe.contentWindow) {
            this._iframe.contentWindow.postMessage({
                type: 'export-closed'
            }, '*');
        }
    }

    async _updateTitleFromLocation() {
        if (this._isExporting) return;

        try {
            let center;

            if (this._frame && this._frame._el && this._frame._el.classList.contains('active')) {
                const frameRect = this._frame._el.getBoundingClientRect();
                if (frameRect.width > 0 && frameRect.height > 0) {
                    const mapRect = this._map.getContainer().getBoundingClientRect();
                    const frameCenterX = (frameRect.left + frameRect.width / 2) - mapRect.left;
                    const frameCenterY = (frameRect.top + frameRect.height / 2) - mapRect.top;
                    center = this._map.unproject([frameCenterX, frameCenterY]);
                }
            }

            if (!center) {
                center = this._map.getCenter();
            }

            const mapZoom = this._map.getZoom();
            const address = await this._reverseGeocode(center.lat, center.lng, mapZoom);

            this._title = address ? `Map of ${address}` : 'Map';

            if (this._iframe && this._iframe.contentWindow) {
                this._iframe.contentWindow.postMessage({
                    type: 'title-update',
                    title: this._title
                }, '*');
            }
        } catch (e) {
            console.warn('Failed to update title from location', e);
            this._title = 'Map';
        }
    }

    async _reverseGeocode(lat, lng, zoom) {
        try {
            const latRounded = Math.round(lat * 100000) / 100000;
            const lngRounded = Math.round(lng * 100000) / 100000;
            const nominatimZoom = Math.max(0, Math.min(18, Math.round(zoom || 15)));
            const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latRounded}&lon=${lngRounded}&zoom=${nominatimZoom}&addressdetails=1`;

            const response = await fetch(url, {
                headers: { 'User-Agent': 'AMChe-Goa-Map-Export/1.0' }
            });

            if (!response.ok) {
                throw new Error(`Nominatim API error: ${response.status}`);
            }

            const data = await response.json();
            if (!data.display_name) return null;

            if (window.attributionControl && data.display_name) {
                window.attributionControl.setLocation(data.display_name);
            }

            const parts = data.display_name.split(',').map(part => part.trim()).filter(part => part.length > 0);

            if (parts.length <= 4) {
                return parts.join(', ');
            }

            const firstLineParts = parts.slice(0, parts.length - 4);
            const lastFourParts = parts.slice(parts.length - 4);
            return firstLineParts.join(', ') + '<br>' + lastFourParts.join(', ');
        } catch (e) {
            console.error('Reverse geocoding failed', e);
            return null;
        }
    }

    async _handleExport(config) {
        this._isExporting = true;
        this._exportCancelled = false;
        this._sendProgress(5, 'Starting export');

        try {
            const format = config.format;

            trackEvent('map_export', { export_type: format });

            if (format === 'pdf') {
                await this._exportPDF(config);
            } else if (format === 'geotiff') {
                await this._exportGeoTIFF(config);
            } else if (format === 'png') {
                await this._exportPNG(config);
            } else if (format === 'jpeg') {
                await this._exportJPEG(config);
            } else if (format === 'html') {
                await this._exportHTML(config);
            } else if (format === 'geojson') {
                await this._exportGeoJSON(config);
            } else if (format === 'kml') {
                await this._exportKML(config);
            } else if (format === 'style') {
                await this._exportStyleJSON(config);
            } else if (format === 'csv') {
                await this._exportCSV(config);
            } else if (format === 'dxf') {
                await this._exportDXF(config);
            }

            if (this._exportCancelled) {
                this._sendProgress(-1, 'Export cancelled');
            } else {
                this._sendProgress(100, 'Export complete');
            }
        } catch (error) {
            if (this._exportCancelled) {
                this._sendProgress(-1, 'Export cancelled');
            } else {
                console.error('Export failed:', error);
                this._sendProgress(-1, `Export failed: ${error.message}`);
            }
        } finally {
            this._isExporting = false;
            this._exportCancelled = false;
            this._restoreFrameVisibility(config);
        }
    }

    _restoreFrameVisibility(config) {
        if (!this._frame) return;

        const shouldShowFrame =
            config.format === 'pdf' ||
            config.format === 'geotiff' ||
            config.format === 'png' ||
            config.format === 'jpeg' ||
            (config.format === 'dxf' && config.includeRaster);

        if (shouldShowFrame && this._iframe && this._iframe.style.display !== 'none') {
            this._frame.show();
        }
    }

    _sendProgress(percent, message) {
        if (this._iframe && this._iframe.contentWindow) {
            this._iframe.contentWindow.postMessage({
                type: 'export-progress',
                percent: percent,
                message: message
            }, '*');
        }
    }

    async _exportPDF(config) {
        const { jsPDF } = await import('jspdf');

        const widthMm = config.width;
        const heightMm = config.height;
        const dpi = config.dpi || 96;
        const includeLegend = config.includeLegend || false;

        const margins = this._parseMargin(config.margin || '10mm');

        let shareUrl = window.location.href;
        if (window.urlManager) {
            shareUrl = window.urlManager.getShareableURL();
        }

        let attributionText = '';
        const attribCtrl = this._map._controls.find(c => c._container && c._container.classList.contains('mapboxgl-ctrl-attrib'));
        if (attribCtrl) {
            attributionText = attribCtrl._container.textContent;
        }

        const contentWidthMm = widthMm - margins.left - margins.right;
        const contentHeightMm = heightMm - margins.top - margins.bottom;
        const targetWidth = Math.round((contentWidthMm * dpi) / 25.4);
        const targetHeight = Math.round((contentHeightMm * dpi) / 25.4);

        const frameBounds = this._frame.getBounds();
        const frameCenter = frameBounds.getCenter();

        const container = this._map.getContainer();
        const originalWidth = container.style.width;
        const originalHeight = container.style.height;
        const originalCenter = this._map.getCenter();
        const originalZoom = this._map.getZoom();
        const originalBearing = this._map.getBearing();
        const originalPitch = this._map.getPitch();

        this._frame.hide();

        this._sendProgress(10, 'Generating QR code');
        let qrDataUrl = null;
        if (config.includeQRCode !== false) {
            try {
                qrDataUrl = await this._getQRCodeDataUrl(shareUrl);
                this._sendProgress(20, 'QR code generated');
            } catch (e) {
                console.warn('Failed to generate QR for PDF', e);
                this._sendProgress(20, 'Skipping QR code');
            }
        } else {
            this._sendProgress(20, 'Skipping QR code');
        }

        this._sendProgress(25, 'Preparing legend');
        let overlayDataUrl = null;
        let overlayWidthMm = 0;
        let overlayHeightMm = 0;

        if (includeLegend) {
            const featurePanelLayers = document.querySelector('.feature-control-layers.map-feature-panel-layers') ||
                document.querySelector('.map-feature-panel-layers');

            const hasContent = featurePanelLayers && (
                featurePanelLayers.children.length > 0 ||
                featurePanelLayers.textContent.trim().length > 0
            );

            if (hasContent) {
                try {
                    const html2canvas = (await import('html2canvas')).default;
                    const parentPanel = featurePanelLayers.closest('.map-feature-panel');
                    const wasHidden = parentPanel && parentPanel.style.display === 'none';
                    const originalDisplay = wasHidden ? 'none' : null;

                    if (wasHidden && parentPanel) {
                        parentPanel.style.display = 'flex';
                        parentPanel.offsetHeight;
                    }

                    const clone = featurePanelLayers.cloneNode(true);

                    const allDetails = clone.querySelectorAll('sl-details');
                    allDetails.forEach(detail => {
                        detail.open = true;
                        const contentContainer = detail.querySelector('.layer-content');
                        if (contentContainer) {
                            contentContainer.style.display = 'block';
                        }
                    });

                    const allTabPanels = clone.querySelectorAll('sl-tab-panel');
                    allTabPanels.forEach(panel => {
                        panel.removeAttribute('hidden');
                        panel.style.display = 'block';
                        panel.style.visibility = 'visible';
                    });

                    const computedStyle = window.getComputedStyle(featurePanelLayers);
                    const targetWidth = parentPanel && parentPanel.offsetWidth > 0
                        ? Math.min(parentPanel.offsetWidth, 350)
                        : 300;

                    clone.style.position = 'absolute';
                    clone.style.left = '0px';
                    clone.style.top = '0px';
                    clone.style.width = `${targetWidth}px`;
                    clone.style.maxWidth = 'none';
                    clone.style.maxHeight = 'none';
                    clone.style.overflow = 'visible';
                    clone.style.backgroundColor = '#ffffff';
                    clone.style.padding = computedStyle.padding;
                    clone.style.margin = '0';
                    clone.style.boxSizing = 'border-box';
                    clone.style.zIndex = '99999';
                    clone.style.fontFamily = computedStyle.fontFamily;
                    clone.style.fontSize = computedStyle.fontSize;
                    clone.style.color = computedStyle.color;
                    clone.style.lineHeight = computedStyle.lineHeight;

                    document.body.appendChild(clone);

                    await new Promise(resolve => requestAnimationFrame(resolve));
                    await new Promise(resolve => requestAnimationFrame(resolve));
                    await new Promise(resolve => setTimeout(resolve, 200));

                    const cloneRect = clone.getBoundingClientRect();
                    const allElements = Array.from(clone.querySelectorAll('*'));
                    let maxBottom = 0;

                    for (const el of allElements) {
                        const style = window.getComputedStyle(el);
                        if (style.display === 'none' || style.visibility === 'hidden') {
                            continue;
                        }

                        const rect = el.getBoundingClientRect();
                        const relativeBottom = rect.bottom - cloneRect.top;

                        const hasText = el.textContent && el.textContent.trim().length > 0;
                        const hasImage = el.querySelector && (el.querySelector('img') || el.querySelector('svg'));
                        const hasVisibleContent = rect.height > 0 && (hasText || hasImage || el.children.length > 0);

                        if (hasVisibleContent && relativeBottom > maxBottom) {
                            maxBottom = relativeBottom;
                        }
                    }

                    const contentHeight = Math.max(maxBottom, clone.scrollHeight);
                    const finalHeight = contentHeight < clone.scrollHeight * 0.8
                        ? contentHeight + 10
                        : clone.scrollHeight;

                    clone.style.left = '-9999px';

                    const canvas = await html2canvas(clone, {
                        backgroundColor: '#ffffff',
                        scale: 2,
                        logging: false,
                        useCORS: true,
                        width: targetWidth,
                        height: finalHeight,
                        windowWidth: targetWidth,
                        windowHeight: finalHeight
                    });

                    document.body.removeChild(clone);

                    overlayDataUrl = canvas.toDataURL('image/png');

                    const logicWidth = canvas.width / 2;
                    const logicHeight = canvas.height / 2;
                    overlayWidthMm = logicWidth * 0.26458;
                    overlayHeightMm = logicHeight * 0.26458;

                    this._sendProgress(40, 'Legend captured');

                    if (wasHidden && parentPanel) {
                        parentPanel.style.display = originalDisplay;
                    }
                } catch (e) {
                    console.warn('Failed to capture overlay', e);
                    this._sendProgress(40, 'Legend capture failed');
                }
            } else {
                this._sendProgress(40, 'No legend to capture');
            }
        } else {
            this._sendProgress(40, 'Skipping legend');
        }

        return new Promise((resolve, reject) => {
            const capture = async () => {
                try {
                    if (this._exportCancelled) {
                        throw new Error('Export cancelled');
                    }

                    this._sendProgress(50, 'Capturing map');
                    const canvas = this._map.getCanvas();
                    const imgData = canvas.toDataURL('image/png');

                    const doc = new jsPDF({
                        orientation: widthMm > heightMm ? 'l' : 'p',
                        unit: 'mm',
                        format: [widthMm, heightMm]
                    });

                    doc.addImage(imgData, 'PNG', margins.left, margins.top, contentWidthMm, contentHeightMm);

                    if (overlayDataUrl && overlayWidthMm > 0 && overlayHeightMm > 0) {
                        const overlayX = margins.left + 5;
                        const overlayY = margins.top + 5;
                        doc.addImage(overlayDataUrl, 'PNG', overlayX, overlayY, overlayWidthMm, overlayHeightMm);
                    }

                    this._sendProgress(70, 'Adding footer');

                    const footerPadding = 2;
                    const elementGap = 4;
                    const textGap = 1.2;
                    const qrSizeMm = 20;
                    const scaleNorthWidthMm = 30;

                    const footerY = heightMm - margins.bottom - qrSizeMm - footerPadding;

                    if (qrDataUrl) {
                        doc.setFillColor(255, 255, 255);
                        doc.setGState(new doc.GState({ opacity: 0.5 }));
                        doc.roundedRect(margins.left + footerPadding, footerY, qrSizeMm, qrSizeMm, 0.5, 0.5, 'F');
                        doc.addImage(qrDataUrl, 'PNG', margins.left + footerPadding, footerY, qrSizeMm, qrSizeMm);
                        doc.setGState(new doc.GState({ opacity: 1.0 }));
                    }

                    const textStartX = margins.left + footerPadding + qrSizeMm + elementGap;
                    const textWidth = contentWidthMm - qrSizeMm - scaleNorthWidthMm - (elementGap * 3) - (footerPadding * 2);
                    let textY = footerY + 1;

                    doc.setGState(new doc.GState({ opacity: 0.5 }));

                    if (this._title) {
                        doc.setFontSize(10);
                        doc.setFont(undefined, 'bold');
                        const titleLines = doc.splitTextToSize(this._title.replace(/<br\s*\/?>/gi, '\n'), textWidth - 4);
                        const titleHeight = titleLines.length * 3.5 + 2;

                        doc.setFillColor(255, 255, 255);
                        doc.roundedRect(textStartX, textY - 1, textWidth, titleHeight, 0.5, 0.5, 'F');

                        doc.setGState(new doc.GState({ opacity: 1.0 }));
                        doc.setTextColor(0, 0, 0);
                        doc.text(titleLines, textStartX + 2, textY + 2);
                        textY += titleHeight + textGap;
                        doc.setGState(new doc.GState({ opacity: 0.5 }));
                    }

                    const date = new Date();
                    const timestamp = date.toLocaleString('en-GB', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    });

                    doc.setFillColor(255, 255, 255);
                    doc.roundedRect(textStartX, textY - 0.5, textWidth, 3.5, 0.5, 0.5, 'F');
                    doc.setGState(new doc.GState({ opacity: 1.0 }));
                    doc.setFontSize(8);
                    doc.setFont(undefined, 'normal');
                    doc.setTextColor(0, 0, 0);
                    doc.text(`Exported at ${timestamp}`, textStartX + 2, textY + 1.5);
                    textY += 3.5 + textGap;

                    if (attributionText) {
                        doc.setGState(new doc.GState({ opacity: 0.5 }));
                        doc.setFillColor(255, 255, 255);
                        doc.roundedRect(textStartX, textY - 0.5, textWidth, 3, 0.5, 0.5, 'F');
                        doc.setGState(new doc.GState({ opacity: 1.0 }));
                        doc.setFontSize(7);
                        doc.setTextColor(0, 0, 0);
                        doc.text(`Data: ${attributionText}`, textStartX + 2, textY + 1.2);
                        textY += 3 + textGap;
                    }

                    doc.setGState(new doc.GState({ opacity: 0.5 }));
                    doc.setFillColor(255, 255, 255);
                    doc.roundedRect(textStartX, textY - 0.5, textWidth, 3, 0.5, 0.5, 'F');
                    doc.setGState(new doc.GState({ opacity: 0.8 }));
                    doc.setFontSize(7);
                    doc.setTextColor(0, 0, 0);
                    const urlLines = doc.splitTextToSize(shareUrl, textWidth - 4);
                    doc.text(urlLines.slice(0, 1), textStartX + 2, textY + 1.2);

                    this._addPDFScaleBar(doc, margins.left + contentWidthMm - scaleNorthWidthMm, footerY, scaleNorthWidthMm);
                    this._addPDFNorthArrow(doc, margins.left + contentWidthMm - scaleNorthWidthMm / 2, footerY + 11, originalBearing);

                    doc.setGState(new doc.GState({ opacity: 1.0 }));

                    this._sendProgress(90, 'Saving PDF');

                    const filename = this._generateFilename('pdf');
                    doc.save(filename);

                    this._sendProgress(95, 'Restoring map');

                    container.style.width = originalWidth;
                    container.style.height = originalHeight;
                    this._map.resize();
                    this._map.jumpTo({
                        center: originalCenter,
                        zoom: originalZoom,
                        bearing: originalBearing,
                        pitch: originalPitch
                    });

                    resolve();
                } catch (error) {
                    container.style.width = originalWidth;
                    container.style.height = originalHeight;
                    this._map.resize();
                    reject(error);
                }
            };

            container.style.width = targetWidth + 'px';
            container.style.height = targetHeight + 'px';
            this._map.resize();

            this._map.once('idle', () => {
                const camera = this._calculateCameraForBounds(frameBounds, targetWidth, targetHeight, originalBearing, originalPitch);

                this._map.jumpTo({
                    ...camera,
                    animate: false
                });

                this._map.once('idle', () => {
                    capture().then(resolve).catch(reject);
                });
            });
        });
    }

    async _getQRCodeDataUrl(text, size = 1024) {
        return new Promise(async (resolve, reject) => {
            try {
                await customElements.whenDefined('sl-qr-code');

                const qr = document.createElement('sl-qr-code');
                qr.value = text;
                qr.size = size;
                qr.style.position = 'fixed';
                qr.style.top = '-9999px';
                qr.style.left = '-9999px';
                document.body.appendChild(qr);

                if (qr.updateComplete) {
                    await qr.updateComplete;
                }

                let attempts = 0;
                const maxAttempts = 50;

                const checkRender = () => {
                    const shadow = qr.shadowRoot;
                    if (shadow) {
                        const svg = shadow.querySelector('svg');
                        const canvas = shadow.querySelector('canvas');

                        if (svg || canvas) {
                            requestAnimationFrame(() => {
                                try {
                                    const padding = Math.round(size * 0.04);
                                    const totalSize = size + (padding * 2);

                                    const outCanvas = document.createElement('canvas');
                                    outCanvas.width = totalSize;
                                    outCanvas.height = totalSize;
                                    const ctx = outCanvas.getContext('2d');

                                    ctx.fillStyle = 'white';
                                    ctx.fillRect(0, 0, totalSize, totalSize);

                                    if (svg) {
                                        const svgData = new XMLSerializer().serializeToString(svg);
                                        const img = new Image();
                                        img.onload = () => {
                                            ctx.drawImage(img, padding, padding, size, size);
                                            document.body.removeChild(qr);
                                            resolve(outCanvas.toDataURL('image/png'));
                                        };
                                        img.onerror = () => {
                                            document.body.removeChild(qr);
                                            reject(new Error('Failed to load QR SVG'));
                                        };
                                        img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
                                    } else if (canvas) {
                                        ctx.drawImage(canvas, padding, padding, size, size);
                                        document.body.removeChild(qr);
                                        resolve(outCanvas.toDataURL('image/png'));
                                    }
                                } catch (err) {
                                    document.body.removeChild(qr);
                                    reject(err);
                                }
                            });
                            return;
                        }
                    }

                    attempts++;
                    if (attempts >= maxAttempts) {
                        document.body.removeChild(qr);
                        reject(new Error('QR code render timeout'));
                        return;
                    }

                    setTimeout(checkRender, 100);
                };

                checkRender();
            } catch (err) {
                reject(err);
            }
        });
    }

    _parseMargin(marginStr) {
        const parts = marginStr.trim().split(/\s+/);
        const values = parts.map(part => {
            const match = part.match(/^([\d.]+)(in|mm|cm|pt|px)?$/);
            if (!match) return 0;

            const value = parseFloat(match[1]);
            const unit = match[2] || 'mm';

            switch (unit) {
                case 'in': return value * 25.4;
                case 'cm': return value * 10;
                case 'pt': return value * 0.3527778;
                case 'px': return value * 0.2645833;
                case 'mm':
                default: return value;
            }
        });

        if (values.length === 1) {
            return { top: values[0], right: values[0], bottom: values[0], left: values[0] };
        } else if (values.length === 2) {
            return { top: values[0], right: values[1], bottom: values[0], left: values[1] };
        } else if (values.length === 3) {
            return { top: values[0], right: values[1], bottom: values[2], left: values[1] };
        } else if (values.length >= 4) {
            return { top: values[0], right: values[1], bottom: values[2], left: values[3] };
        }

        return { top: 10, right: 10, bottom: 10, left: 10 };
    }

    async _exportGeoTIFF(config) {
        this._sendProgress(10, 'Preparing GeoTIFF export');

        const widthMm = config.width || 297;
        const heightMm = config.height || 210;
        const dpi = config.dpi || 96;

        const width = Math.round((widthMm * dpi) / 25.4);
        const height = Math.round((heightMm * dpi) / 25.4);

        const bounds = this._frame.getBounds();
        const center = bounds.getCenter();
        const canvas = this._map.getCanvas();

        const container = this._map.getContainer();
        const originalWidth = container.style.width;
        const originalHeight = container.style.height;
        const originalCenter = this._map.getCenter();
        const originalZoom = this._map.getZoom();
        const originalBearing = this._map.getBearing();
        const originalPitch = this._map.getPitch();

        this._frame.hide();

        this._sendProgress(30, 'Capturing map');

        container.style.width = width + 'px';
        container.style.height = height + 'px';
        this._map.resize();

        await new Promise(resolve => {
            this._map.once('idle', resolve);
        });

        const camera = this._calculateCameraForBounds(bounds, width, height, originalBearing, originalPitch);

        this._map.jumpTo({
            ...camera,
            animate: false
        });

        await new Promise(resolve => {
            this._map.once('idle', resolve);
        });

        this._sendProgress(60, 'Generating image');

        const imageData = canvas.toDataURL('image/png');
        const imageBlob = await fetch(imageData).then(r => r.blob());

        this._sendProgress(80, 'Creating world file');

        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        const pixelSizeX = (ne.lng - sw.lng) / width;
        const pixelSizeY = (ne.lat - sw.lat) / height;

        const worldFileContent = [
            pixelSizeX.toFixed(8),
            '0.0',
            '0.0',
            (-pixelSizeY).toFixed(8),
            sw.lng.toFixed(8),
            ne.lat.toFixed(8)
        ].join('\n');

        const filename = this._generateFilename('png');
        const worldFilename = filename.replace('.png', '.pgw');

        this._downloadFile(imageBlob, filename, 'image/png');
        this._downloadFile(worldFileContent, worldFilename, 'text/plain');

        this._sendProgress(95, 'Restoring map');

        container.style.width = originalWidth;
        container.style.height = originalHeight;
        this._map.resize();
        this._map.jumpTo({
            center: originalCenter,
            zoom: originalZoom,
            bearing: originalBearing,
            pitch: originalPitch
        });

        this._sendProgress(100, 'GeoTIFF exported');
    }

    _calculateCameraForBounds(bounds, containerWidth, containerHeight, bearing = 0, pitch = 0) {
        try {
            const camera = this._map.cameraForBounds(bounds, {
                padding: 0,
                bearing: bearing,
                pitch: pitch
            });

            if (!camera || camera.zoom === undefined || !isFinite(camera.zoom)) {
                console.warn('cameraForBounds returned invalid camera');
                return {
                    center: bounds.getCenter(),
                    zoom: this._map.getZoom(),
                    bearing: bearing,
                    pitch: pitch
                };
            }

            return camera;
        } catch (e) {
            console.error('Failed to calculate camera for bounds:', e);
            return {
                center: bounds.getCenter(),
                zoom: this._map.getZoom(),
                bearing: bearing,
                pitch: pitch
            };
        }
    }

    _addPDFScaleBar(doc, x, y, widthMm) {
        const center = this._map.getCenter();
        const zoom = this._map.getZoom();
        const metersPerPixel = 40075016.686 * Math.abs(Math.cos(center.lat * Math.PI / 180)) / Math.pow(2, zoom + 8);
        const pixelsPerMm = 96 / 25.4;
        const scaleBarWidthMm = widthMm * 0.8;
        const scaleMeters = Math.round(metersPerPixel * scaleBarWidthMm * pixelsPerMm);

        let scaleText;
        if (scaleMeters >= 1000) {
            scaleText = `${(scaleMeters / 1000).toFixed(0)} km`;
        } else {
            scaleText = `${scaleMeters} m`;
        }

        doc.setGState(new doc.GState({ opacity: 0.5 }));
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(x, y, widthMm, 8, 0.5, 0.5, 'F');

        doc.setGState(new doc.GState({ opacity: 1.0 }));
        doc.setFontSize(6);
        doc.setTextColor(0, 0, 0);
        doc.text(scaleText, x + widthMm / 2, y + 2.5, { align: 'center' });

        doc.setFillColor(0, 0, 0);
        doc.rect(x + (widthMm - scaleBarWidthMm) / 2, y + 4, scaleBarWidthMm, 0.3, 'F');
        doc.rect(x + (widthMm - scaleBarWidthMm) / 2, y + 4, 0.3, 2, 'F');
        doc.rect(x + (widthMm - scaleBarWidthMm) / 2 + scaleBarWidthMm - 0.3, y + 4, 0.3, 2, 'F');
    }

    _addPDFNorthArrow(doc, x, y, bearing) {
        const arrowSize = 12;

        doc.setGState(new doc.GState({ opacity: 0.5 }));
        doc.setFillColor(255, 255, 255);
        doc.circle(x, y, arrowSize / 2, 'F');

        doc.setGState(new doc.GState({ opacity: 1.0 }));

        const centerX = x;
        const centerY = y;
        const radius = arrowSize / 2 * 0.8;
        const angleRad = -bearing * Math.PI / 180;

        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.15);
        doc.setGState(new doc.GState({ opacity: 0.3 }));
        doc.circle(centerX, centerY, radius, 'S');

        doc.setGState(new doc.GState({ opacity: 1.0 }));
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 0, 0);

        const northX = centerX + radius * Math.sin(angleRad);
        const northY = centerY - radius * Math.cos(angleRad);
        doc.text('N', northX, northY - 1.5, { align: 'center' });

        doc.setGState(new doc.GState({ opacity: 0.3 }));
        doc.setFillColor(0, 0, 0);

        const southX = centerX - radius * 0.6 * Math.sin(angleRad);
        const southY = centerY + radius * 0.6 * Math.cos(angleRad);
        doc.rect(southX - 0.125, southY - 1.5, 0.25, 3, 'F');

        const eastAngle = angleRad + Math.PI / 2;
        const eastX = centerX + radius * 0.6 * Math.sin(eastAngle);
        const eastY = centerY - radius * 0.6 * Math.cos(eastAngle);
        doc.rect(eastX - 1.5, eastY - 0.125, 3, 0.25, 'F');

        const westAngle = angleRad - Math.PI / 2;
        const westX = centerX + radius * 0.6 * Math.sin(westAngle);
        const westY = centerY - radius * 0.6 * Math.cos(westAngle);
        doc.rect(westX - 1.5, westY - 0.125, 3, 0.25, 'F');

        doc.setGState(new doc.GState({ opacity: 1.0 }));
    }

    async _exportPNG(config) {
        this._sendProgress(10, 'Preparing PNG export');

        const widthMm = config.width;
        const heightMm = config.height;
        const dpi = config.dpi || 96;

        const targetWidth = Math.round((widthMm * dpi) / 25.4);
        const targetHeight = Math.round((heightMm * dpi) / 25.4);

        const frameBounds = this._frame.getBounds();
        const frameCenter = frameBounds.getCenter();

        const container = this._map.getContainer();
        const originalWidth = container.style.width;
        const originalHeight = container.style.height;
        const originalCenter = this._map.getCenter();
        const originalZoom = this._map.getZoom();
        const originalBearing = this._map.getBearing();
        const originalPitch = this._map.getPitch();

        this._frame.hide();

        return new Promise((resolve, reject) => {
            const capture = async () => {
                try {
                    if (this._exportCancelled) {
                        throw new Error('Export cancelled');
                    }

                    await new Promise(resolve => setTimeout(resolve, 100));

                    this._sendProgress(50, 'Capturing map');
                    const canvas = this._map.getCanvas();
                    let dataUrl = canvas.toDataURL('image/png');

                    const actualPixelWidth = canvas.width;
                    const actualPixelHeight = canvas.height;

                    this._sendProgress(60, 'Adding attribution');
                    dataUrl = await this._addFooterToRaster(dataUrl, actualPixelWidth, actualPixelHeight, frameCenter, originalBearing, dpi, config);

                    const blob = await fetch(dataUrl).then(r => r.blob());

                    this._sendProgress(80, 'Downloading file');
                    const filename = this._generateFilename('png');
                    this._downloadFile(blob, filename, 'image/png');

                    this._sendProgress(95, 'Restoring map');

                    container.style.width = originalWidth;
                    container.style.height = originalHeight;
                    this._map.resize();
                    this._map.jumpTo({
                        center: originalCenter,
                        zoom: originalZoom,
                        bearing: originalBearing,
                        pitch: originalPitch
                    });

                    resolve();
                } catch (error) {
                    container.style.width = originalWidth;
                    container.style.height = originalHeight;
                    this._map.resize();
                    reject(error);
                }
            };

            container.style.width = targetWidth + 'px';
            container.style.height = targetHeight + 'px';
            this._map.resize();

            this._map.once('idle', () => {
                const camera = this._calculateCameraForBounds(frameBounds, targetWidth, targetHeight, originalBearing, originalPitch);

                this._map.jumpTo({
                    ...camera,
                    animate: false
                });

                this._map.once('idle', () => {
                    capture().then(resolve).catch(reject);
                });
            });
        });
    }

    async _exportJPEG(config) {
        this._sendProgress(10, 'Preparing JPEG export');

        const widthMm = config.width;
        const heightMm = config.height;
        const dpi = config.dpi || 96;

        const targetWidth = Math.round((widthMm * dpi) / 25.4);
        const targetHeight = Math.round((heightMm * dpi) / 25.4);

        const frameBounds = this._frame.getBounds();
        const frameCenter = frameBounds.getCenter();

        const container = this._map.getContainer();
        const originalWidth = container.style.width;
        const originalHeight = container.style.height;
        const originalCenter = this._map.getCenter();
        const originalZoom = this._map.getZoom();
        const originalBearing = this._map.getBearing();
        const originalPitch = this._map.getPitch();

        this._frame.hide();

        return new Promise((resolve, reject) => {
            const capture = async () => {
                try {
                    if (this._exportCancelled) {
                        throw new Error('Export cancelled');
                    }

                    await new Promise(resolve => setTimeout(resolve, 100));

                    this._sendProgress(50, 'Capturing map');
                    const canvas = this._map.getCanvas();
                    let dataUrl = canvas.toDataURL('image/png');

                    const actualPixelWidth = canvas.width;
                    const actualPixelHeight = canvas.height;

                    this._sendProgress(60, 'Adding attribution');
                    dataUrl = await this._addFooterToRaster(dataUrl, actualPixelWidth, actualPixelHeight, frameCenter, originalBearing, dpi, config);

                    this._sendProgress(70, 'Converting to JPEG');
                    const tempImg = new Image();
                    await new Promise((resolve, reject) => {
                        tempImg.onload = resolve;
                        tempImg.onerror = reject;
                        tempImg.src = dataUrl;
                    });

                    const jpegCanvas = document.createElement('canvas');
                    jpegCanvas.width = actualPixelWidth;
                    jpegCanvas.height = actualPixelHeight;
                    const ctx = jpegCanvas.getContext('2d');
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, actualPixelWidth, actualPixelHeight);
                    ctx.drawImage(tempImg, 0, 0);
                    dataUrl = jpegCanvas.toDataURL('image/jpeg', 0.92);

                    const blob = await fetch(dataUrl).then(r => r.blob());

                    this._sendProgress(80, 'Downloading file');
                    const filename = this._generateFilename('jpg');
                    this._downloadFile(blob, filename, 'image/jpeg');

                    this._sendProgress(95, 'Restoring map');

                    container.style.width = originalWidth;
                    container.style.height = originalHeight;
                    this._map.resize();
                    this._map.jumpTo({
                        center: originalCenter,
                        zoom: originalZoom,
                        bearing: originalBearing,
                        pitch: originalPitch
                    });

                    resolve();
                } catch (error) {
                    container.style.width = originalWidth;
                    container.style.height = originalHeight;
                    this._map.resize();
                    reject(error);
                }
            };

            container.style.width = targetWidth + 'px';
            container.style.height = targetHeight + 'px';
            this._map.resize();

            this._map.once('idle', () => {
                const camera = this._calculateCameraForBounds(frameBounds, targetWidth, targetHeight, originalBearing, originalPitch);

                this._map.jumpTo({
                    ...camera,
                    animate: false
                });

                this._map.once('idle', () => {
                    capture().then(resolve).catch(reject);
                });
            });
        });
    }

    async _exportHTML(config) {
        this._sendProgress(20, 'Generating HTML');

        const center = this._map.getCenter();
        const zoom = this._map.getZoom();
        const bearing = this._map.getBearing();
        const pitch = this._map.getPitch();
        const style = this._map.getStyle();

        const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>${this._title || 'Map Export'}</title>
    <meta name="viewport" content="initial-scale=1,maximum-scale=1,user-scalable=no">
    <link href="https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.css" rel="stylesheet">
    <script src="https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.js"></script>
    <style>
        body { margin: 0; padding: 0; }
        #map { position: absolute; top: 0; bottom: 0; width: 100%; }
    </style>
</head>
<body>
    <div id="map"></div>
    <script>
        mapboxgl.accessToken = '${mapboxgl.accessToken}';
        const map = new mapboxgl.Map({
            container: 'map',
            style: ${JSON.stringify(style)},
            center: [${center.lng}, ${center.lat}],
            zoom: ${zoom},
            bearing: ${bearing},
            pitch: ${pitch}
        });
        map.addControl(new mapboxgl.NavigationControl());
        map.addControl(new mapboxgl.ScaleControl());
    </script>
</body>
</html>`;

        this._sendProgress(80, 'Downloading file');
        const filename = this._generateFilename('html');
        this._downloadFile(html, filename, 'text/html');
    }

    async _exportGeoJSON(config) {
        this._sendProgress(20, 'Collecting features');

        let features;
        let filename;

        if (config.exportSelectedOnly && (config.customSelectedFeatures || this._hasSelectedFeatures())) {
            const selectedFeatures = config.customSelectedFeatures || this._getSelectedFeatures();
            features = selectedFeatures.map(item => item.feature);
            filename = this._generateFilenameFromFeatures(selectedFeatures, 'geojson');
        } else {
            features = [];
            const activeLayerIds = this._getActiveStyleLayerIds();
            const layers = this._map.getStyle().layers.filter(l =>
                activeLayerIds.has(l.id) &&
                (l.type === 'fill' || l.type === 'line' || l.type === 'circle' || l.type === 'symbol')
            );

            for (const layer of layers) {
                const sourceFeatures = this._map.querySourceFeatures(layer.source, {
                    sourceLayer: layer['source-layer']
                });
                features.push(...sourceFeatures);
            }
            filename = this._generateFilename('geojson');
        }

        const geojson = {
            type: 'FeatureCollection',
            features: features
        };

        this._sendProgress(80, 'Downloading file');
        this._downloadFile(JSON.stringify(geojson, null, 2), filename, 'application/geo+json');
    }

    async _exportKML(config) {
        this._sendProgress(20, 'Collecting features');

        let features;
        let filename;

        if (config.exportSelectedOnly && (config.customSelectedFeatures || this._hasSelectedFeatures())) {
            const selectedFeatures = config.customSelectedFeatures || this._getSelectedFeatures();
            features = selectedFeatures.map(item => item.feature);
            filename = this._generateFilenameFromFeatures(selectedFeatures, 'kml');
        } else {
            features = [];
            const activeLayerIds = this._getActiveStyleLayerIds();
            const layers = this._map.getStyle().layers.filter(l =>
                activeLayerIds.has(l.id) &&
                (l.type === 'fill' || l.type === 'line' || l.type === 'circle' || l.type === 'symbol')
            );

            for (const layer of layers) {
                const sourceFeatures = this._map.querySourceFeatures(layer.source, {
                    sourceLayer: layer['source-layer']
                });
                features.push(...sourceFeatures);
            }
            filename = this._generateFilename('kml');
        }

        this._sendProgress(50, 'Converting to KML');

        const tokml = (await import('tokml')).default;
        const geojson = {
            type: 'FeatureCollection',
            features: features
        };
        const kml = tokml(geojson);

        this._sendProgress(80, 'Downloading file');
        this._downloadFile(kml, filename, 'application/vnd.google-earth.kml+xml');
    }

    async _exportStyleJSON(config) {
        this._sendProgress(50, 'Getting style');
        const style = this._map.getStyle();

        this._sendProgress(80, 'Downloading file');
        const filename = this._generateFilename('json');
        this._downloadFile(JSON.stringify(style, null, 2), filename, 'application/json');
    }

    async _exportCSV(config) {
        this._sendProgress(20, 'Collecting features');

        let features;
        let filename;

        const featuresWithMetadata = [];

        if (config.exportSelectedOnly && (config.customSelectedFeatures || this._hasSelectedFeatures())) {
            console.log('CSV Export: Using selected features');
            const selectedFeatures = config.customSelectedFeatures || this._getSelectedFeatures();
            console.log(`CSV Export: Found ${selectedFeatures.length} selected features`);

            for (const item of selectedFeatures) {
                featuresWithMetadata.push({
                    feature: item.feature,
                    layerId: item.layerId,
                    layerTitle: item.layerConfig?.title || item.layerId,
                    layerConfig: item.layerConfig
                });
            }
            filename = this._generateFilenameFromFeatures(selectedFeatures, 'csv');
        } else {
            console.log('CSV Export: Using all rendered features');
            const activeLayerIds = Array.from(this._getActiveStyleLayerIds());
            const allFeatures = activeLayerIds.length
                ? this._map.queryRenderedFeatures({ layers: activeLayerIds })
                : [];
            console.log(`CSV Export: Found ${allFeatures.length} rendered features`);

            const validFeatures = allFeatures.filter(f => f.geometry && f.geometry.type);
            console.log(`CSV Export: After filtering: ${validFeatures.length} features with geometry`);

            for (const feature of validFeatures) {
                const layerConfig = this._getLayerConfigById(feature.layer?.id);
                featuresWithMetadata.push({
                    feature: feature,
                    layerId: feature.layer?.id,
                    layerTitle: feature.layer?.id,
                    layerConfig: layerConfig
                });
            }
            filename = this._generateFilename('csv');
        }

        this._sendProgress(50, 'Processing features');

        console.log(`CSV Export: Found ${featuresWithMetadata.length} features to process`);
        if (featuresWithMetadata.length > 0) {
            console.log('First feature:', featuresWithMetadata[0]);
        }

        const handlerLoader = new InspectionHandlerLoader();
        const rows = [];
        const geometryField = config.geometryField || 'xy';
        const allKeys = new Set(
            geometryField === 'wkt'
                ? ['WKT', 'layer_url']
                : ['X', 'Y', 'layer_url']
        );

        for (const item of featuresWithMetadata) {
            const feature = item.feature;

            if (!feature.geometry || !feature.geometry.type) {
                console.log('Skipping feature without geometry:', feature);
                continue;
            }

            const centroid = this._calculateCentroid(feature.geometry);

            const center = this._map.getCenter();
            const zoom = this._map.getZoom();
            const bearing = this._map.getBearing();
            const pitch = this._map.getPitch();

            const baseUrl = window.location.origin + window.location.pathname;
            const params = new URLSearchParams();

            if (window.urlManager) {
                const currentUrl = new URL(window.urlManager.getShareableURL());
                for (const [key, value] of currentUrl.searchParams.entries()) {
                    if (key !== 'lat' && key !== 'lng' && key !== 'zoom') {
                        params.set(key, value);
                    }
                }
            }

            params.set('lat', centroid.lat.toFixed(6));
            params.set('lng', centroid.lng.toFixed(6));
            params.set('zoom', '14');

            if (bearing !== 0) params.set('bearing', bearing.toFixed(2));
            if (pitch !== 0) params.set('pitch', pitch.toFixed(2));

            const amcheUrl = `${baseUrl}?${params.toString()}`;

            const row = {
                ...feature.properties
            };

            if (geometryField === 'wkt') {
                row.WKT = this._geometryToWKT(feature.geometry);
            } else {
                row.X = centroid.lng.toFixed(6);
                row.Y = centroid.lat.toFixed(6);
            }

            row.layer_url = amcheUrl;

            if (item.layerId) {
                row.layer_id = item.layerId;
                allKeys.add('layer_id');
            }
            if (item.layerTitle) {
                row.layer_title = item.layerTitle;
                allKeys.add('layer_title');
            }

            if (item.layerConfig?.inspect?.onClick) {
                const handlerName = item.layerConfig.inspect.onClick;
                const atlasName = this._getAtlasNameForLayer(item.layerId, item.layerConfig);
                const fieldName = `layer_${handlerName}`;

                allKeys.add(fieldName);

                console.log(`CSV Export: Executing handler "${handlerName}" for layer "${item.layerId}" in atlas "${atlasName}"`);

                try {
                    const handlerOutput = await handlerLoader.executeHandler(
                        atlasName,
                        handlerName,
                        {
                            feature: feature,
                            layerId: item.layerId,
                            layerConfig: item.layerConfig,
                            map: this._map,
                            lngLat: { lng: centroid.lng, lat: centroid.lat }
                        }
                    );

                    console.log(`CSV Export: Handler output length: ${handlerOutput?.length || 0}`);

                    if (handlerOutput) {
                        const extractedData = await this._extractHandlerData(handlerOutput, feature);
                        console.log(`CSV Export: Extracted data: "${extractedData.substring(0, 100)}..."`);
                        row[fieldName] = extractedData;
                    } else {
                        console.log('CSV Export: Handler returned null/empty');
                        row[fieldName] = '';
                    }
                } catch (error) {
                    console.warn(`CSV Export: Failed to execute handler ${handlerName}:`, error);
                    row[fieldName] = '[Handler Error]';
                }
            } else {
                console.log(`CSV Export: No handler configured for layer ${item.layerId}`);
            }

            Object.keys(feature.properties || {}).forEach(key => allKeys.add(key));
            rows.push(row);
        }

        this._sendProgress(70, 'Generating CSV');

        const headers = Array.from(allKeys);
        const csvLines = [];

        csvLines.push(headers.map(h => this._escapeCsvValue(h)).join(','));

        for (const row of rows) {
            const values = headers.map(header => {
                const value = row[header];
                return this._escapeCsvValue(value);
            });
            csvLines.push(values.join(','));
        }

        const csvContent = csvLines.join('\n');

        this._sendProgress(90, 'Downloading file');
        this._downloadFile(csvContent, filename, 'text/csv');
    }

    _calculateCentroid(geometry) {
        if (!geometry || !geometry.type) {
            const center = this._map.getCenter();
            return { lng: center.lng, lat: center.lat };
        }

        if (geometry.type === 'Point') {
            return {
                lng: geometry.coordinates[0],
                lat: geometry.coordinates[1]
            };
        }

        if (geometry.type === 'MultiPoint') {
            const coords = geometry.coordinates;
            const lng = coords.reduce((sum, c) => sum + c[0], 0) / coords.length;
            const lat = coords.reduce((sum, c) => sum + c[1], 0) / coords.length;
            return { lng, lat };
        }

        if (geometry.type === 'LineString') {
            const coords = geometry.coordinates;
            const midIndex = Math.floor(coords.length / 2);
            return {
                lng: coords[midIndex][0],
                lat: coords[midIndex][1]
            };
        }

        if (geometry.type === 'MultiLineString') {
            const allCoords = geometry.coordinates.flat();
            const lng = allCoords.reduce((sum, c) => sum + c[0], 0) / allCoords.length;
            const lat = allCoords.reduce((sum, c) => sum + c[1], 0) / allCoords.length;
            return { lng, lat };
        }

        if (geometry.type === 'Polygon') {
            const coords = geometry.coordinates[0];
            const lng = coords.reduce((sum, c) => sum + c[0], 0) / coords.length;
            const lat = coords.reduce((sum, c) => sum + c[1], 0) / coords.length;
            return { lng, lat };
        }

        if (geometry.type === 'MultiPolygon') {
            const allCoords = geometry.coordinates.flat(2);
            const lng = allCoords.reduce((sum, c) => sum + c[0], 0) / allCoords.length;
            const lat = allCoords.reduce((sum, c) => sum + c[1], 0) / allCoords.length;
            return { lng, lat };
        }

        return { lng: 0, lat: 0 };
    }

    _geometryToWKT(geometry) {
        if (!geometry || !geometry.type) {
            return '';
        }

        const coordsToString = (coords) => coords.join(' ');
        const ringToString = (ring) => '(' + ring.map(coordsToString).join(', ') + ')';

        switch (geometry.type) {
            case 'Point':
                return `POINT(${coordsToString(geometry.coordinates)})`;

            case 'MultiPoint':
                return `MULTIPOINT(${geometry.coordinates.map(coordsToString).join(', ')})`;

            case 'LineString':
                return `LINESTRING(${geometry.coordinates.map(coordsToString).join(', ')})`;

            case 'MultiLineString':
                return `MULTILINESTRING(${geometry.coordinates.map(ring => ringToString(ring)).join(', ')})`;

            case 'Polygon':
                return `POLYGON(${geometry.coordinates.map(ring => ringToString(ring)).join(', ')})`;

            case 'MultiPolygon':
                return `MULTIPOLYGON(${geometry.coordinates.map(polygon =>
                    '(' + polygon.map(ring => ringToString(ring)).join(', ') + ')'
                ).join(', ')})`;

            default:
                return '';
        }
    }

    _escapeCsvValue(value) {
        if (value === null || value === undefined) {
            return '';
        }

        const stringValue = String(value);

        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return '"' + stringValue.replace(/"/g, '""') + '"';
        }

        return stringValue;
    }

    async _exportDXF(config) {
        const { DXFConverter } = await import('./dxf-converter.js');
        const { DXFCoordinateTransformer } = await import('./dxf-coordinate-transformer.js');

        this._sendProgress(10, 'Preparing data');

        if (!config.includeRaster) {
            let features;
            let filename;

            if (config.exportSelectedOnly && (config.customSelectedFeatures || this._hasSelectedFeatures())) {
                const selectedFeatures = config.customSelectedFeatures || this._getSelectedFeatures();
                features = selectedFeatures.map(item => item.feature);
                filename = this._generateFilenameFromFeatures(selectedFeatures, 'dxf');
            } else {
                const activeLayerIds = Array.from(this._getActiveStyleLayerIds());
                features = activeLayerIds.length
                    ? this._map.queryRenderedFeatures({ layers: activeLayerIds })
                    : [];
                filename = this._generateFilename('dxf');
            }

            this._sendProgress(30, 'Converting coordinates');

            const mapCenter = this._map.getCenter();
            const transformer = new DXFCoordinateTransformer({
                coordSystem: config.coordSystem || 'local',
                mapCenter: mapCenter,
                map: this._map
            });

            const transformedFeatures = transformer.transformFeatures(features);

            this._sendProgress(60, 'Generating DXF');

            const geojson = {
                type: 'FeatureCollection',
                features: transformedFeatures
            };

            const dxfContent = DXFConverter.geoJsonToDxf(geojson, {
                title: this._title || 'Exported Features',
                coordSystem: config.coordSystem || 'local',
                units: transformer.getUnits()
            });

            this._sendProgress(90, 'Downloading');

            this._downloadFile(dxfContent, filename, 'application/dxf');
        } else {
            await this._exportDXFHybrid(config);
        }

        this._sendProgress(100, 'Complete');
    }

    async _exportDXFHybrid(config) {
        const { DXFConverter } = await import('./dxf-converter.js');
        const { DXFCoordinateTransformer } = await import('./dxf-coordinate-transformer.js');

        this._sendProgress(10, 'Preparing capture');

        if (this._exportCancelled) {
            throw new Error('Export cancelled');
        }

        if (!this._frame || !this._frame._el) {
            throw new Error('Export frame is not initialized');
        }

        const frameRect = this._frame._el.getBoundingClientRect();

        if (!frameRect.width || !frameRect.height || frameRect.width === 0 || frameRect.height === 0) {
            throw new Error('Export frame dimensions are invalid. Please ensure the frame is visible before exporting.');
        }

        const originalStyle = this._map.getContainer().style.cssText;
        const originalCenter = this._map.getCenter();
        const originalZoom = this._map.getZoom();
        const originalBearing = this._map.getBearing();
        const originalPitch = this._map.getPitch();

        let terrainControl = null;
        let originalTerrainEnabled = false;

        const controls = this._map._controls || [];
        for (const control of controls) {
            if (control.constructor.name === 'Terrain3DControl') {
                terrainControl = control;
                originalTerrainEnabled = control.getEnabled();
                break;
            }
        }

        const mapRect = this._map.getContainer().getBoundingClientRect();

        const frameCenterX = (frameRect.left + frameRect.width / 2) - mapRect.left;
        const frameCenterY = (frameRect.top + frameRect.height / 2) - mapRect.top;
        const frameWidth = frameRect.width;
        const frameHeight = frameRect.height;

        this._sendProgress(15, 'Resetting camera');

        if (terrainControl && originalTerrainEnabled) {
            terrainControl.setEnabled(false);
        }

        const needsReset = originalBearing !== 0 || originalPitch !== 0;

        if (needsReset) {
            this._map.setBearing(0);
            this._map.setPitch(0);

            await Promise.race([
                new Promise(resolve => this._map.once('moveend', resolve)),
                new Promise(resolve => setTimeout(resolve, 1000))
            ]);
        }

        this._sendProgress(20, 'Calculating frame bounds');

        const frameCenter = this._map.unproject([frameCenterX, frameCenterY]);

        this._sendProgress(30, 'Processing raster layers');

        this._frame.hide();

        const widthMm = config.width;
        const heightMm = config.height;
        const dpi = config.dpi || 96;
        const targetWidth = Math.round((widthMm * dpi) / 25.4);
        const targetHeight = Math.round((heightMm * dpi) / 25.4);

        const container = this._map.getContainer();
        Object.assign(container.style, {
            width: targetWidth + 'px',
            height: targetHeight + 'px',
            position: 'fixed',
            top: '0',
            left: '0',
            zIndex: '-9999'
        });

        this._map.resize();

        const scaleFactor = targetWidth / frameWidth;
        const newZoom = originalZoom + Math.log2(scaleFactor);

        this._map.jumpTo({
            center: frameCenter,
            zoom: newZoom,
            bearing: 0,
            pitch: 0,
            animate: false
        });

        await new Promise(resolve => setTimeout(resolve, 100));

        await new Promise(resolve => {
            this._map.once('idle', resolve);
        });

        this._sendProgress(50, 'Rendering raster');

        if (this._exportCancelled) {
            throw new Error('Export cancelled');
        }

        const nw = this._map.unproject([0, 0]);
        const se = this._map.unproject([targetWidth, targetHeight]);

        const canvas = this._map.getCanvas();
        let imageDataUrl = canvas.toDataURL('image/png');

        const actualPixelWidth = canvas.width;
        const actualPixelHeight = canvas.height;

        this._sendProgress(52, 'Adding attribution');

        imageDataUrl = await this._addFooterToRaster(imageDataUrl, actualPixelWidth, actualPixelHeight, frameCenter, originalBearing, dpi);

        this._sendProgress(55, 'Extracting vector features');

        const activeLayerIds = Array.from(this._getActiveStyleLayerIds());
        const features = activeLayerIds.length
            ? this._map.queryRenderedFeatures({ layers: activeLayerIds })
            : [];
        const filteredFeatures = features.filter(feature => {
            if (feature.geometry.type === 'Point') {
                const [lng, lat] = feature.geometry.coordinates;
                return lng >= nw.lng && lng <= se.lng && lat <= nw.lat && lat >= se.lat;
            }
            return true;
        });

        this._sendProgress(60, 'Calculating dimensions');

        const transformer = new DXFCoordinateTransformer({
            coordSystem: config.coordSystem || 'local',
            mapCenter: frameCenter,
            map: this._map,
            bounds: { nw, se }
        });

        const transformedNW = transformer._transformCoordinate([nw.lng, nw.lat, 0]);
        const transformedSE = transformer._transformCoordinate([se.lng, se.lat, 0]);

        const imageDimensions = {
            width: Math.abs(transformedSE[0] - transformedNW[0]),
            height: Math.abs(transformedNW[1] - transformedSE[1])
        };

        container.style.cssText = originalStyle;
        this._map.resize();
        this._map.jumpTo({
            center: originalCenter,
            zoom: originalZoom,
            bearing: originalBearing,
            pitch: originalPitch
        });

        if (terrainControl && originalTerrainEnabled) {
            terrainControl.setEnabled(true);
        }

        this._frame.show();

        this._sendProgress(70, 'Converting coordinates');

        const transformedFeatures = transformer.transformFeatures(filteredFeatures);

        this._sendProgress(85, 'Generating DXF');

        const geojson = {
            type: 'FeatureCollection',
            features: transformedFeatures
        };

        let baseFilename = this._generateFilename('dxf').replace('.dxf', '');
        const rasterFilename = `${baseFilename}_raster.png`;

        const dxfContent = DXFConverter.geoJsonToDxf(geojson, {
            title: this._title || 'Map Export',
            coordSystem: config.coordSystem || 'local',
            units: transformer.getUnits(),
            rasterImage: {
                dataUrl: imageDataUrl,
                width: imageDimensions.width,
                height: imageDimensions.height,
                position: [0, 0],
                filename: rasterFilename,
                pixelWidth: actualPixelWidth,
                pixelHeight: actualPixelHeight
            }
        });

        this._sendProgress(90, 'Downloading DXF');

        this._downloadFile(dxfContent, `${baseFilename}.dxf`, 'application/dxf');

        this._sendProgress(93, 'Downloading raster');

        const imageBlob = await fetch(imageDataUrl).then(r => r.blob());
        this._downloadFile(imageBlob, rasterFilename, 'image/png');

        this._sendProgress(95, 'Creating world file');

        const worldFileContent = this._generateWorldFile(imageDimensions, transformer, frameCenter, actualPixelWidth, actualPixelHeight);
        this._downloadFile(worldFileContent, `${baseFilename}_raster.pgw`, 'text/plain');
    }

    async _loadFooterTemplate() {
        if (this._footerTemplateCache) {
            return this._footerTemplateCache;
        }

        try {
            const response = await fetch('map-export-layout.html');
            if (!response.ok) {
                throw new Error('Failed to load template');
            }
            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            const template = doc.getElementById('export-footer-template');
            if (!template) {
                throw new Error('Footer template not found');
            }

            const styles = doc.querySelector('style');

            this._footerTemplateCache = {
                footer: template,
                styles: styles
            };

            return this._footerTemplateCache;
        } catch (e) {
            console.error('Failed to load footer template:', e);
            return null;
        }
    }

    _formatUrlForDisplay(url) {
        try {
            const urlObj = new URL(url);
            const parts = [];

            parts.push(urlObj.origin + urlObj.pathname);

            const params = new URLSearchParams(urlObj.search);
            const paramParts = [];

            for (const [key, value] of params.entries()) {
                if (key === 'layers') {
                    const decodedLayers = decodeURIComponent(value);
                    const layerItems = [];
                    let current = '';
                    let depth = 0;

                    for (let i = 0; i < decodedLayers.length; i++) {
                        const char = decodedLayers[i];
                        if (char === '{' || char === '[') {
                            depth++;
                            current += char;
                        } else if (char === '}' || char === ']') {
                            depth--;
                            current += char;
                        } else if (char === ',' && depth === 0) {
                            if (current.trim()) {
                                layerItems.push(current.trim());
                            }
                            current = '';
                        } else {
                            current += char;
                        }
                    }
                    if (current.trim()) {
                        layerItems.push(current.trim());
                    }

                    const filteredLayers = layerItems.filter(layer => {
                        return !layer.includes('"id":"selection"') &&
                               !layer.includes("'id':'selection'");
                    });

                    if (filteredLayers.length > 0) {
                        paramParts.push(`layers=${filteredLayers.join(', ')}`);
                    }
                } else {
                    const displayValue = decodeURIComponent(value);
                    paramParts.push(`${key}=${displayValue}`);
                }
            }

            if (paramParts.length > 0) {
                parts.push('?' + paramParts.join(' & '));
            }

            if (urlObj.hash) {
                parts.push(urlObj.hash);
            }

            return parts.join(' ');
        } catch (e) {
            return url;
        }
    }

    async _addFooterToRaster(mapImageDataUrl, width, height, center, bearing, dpi = 96, config = {}) {
        try {
            const html2canvas = (await import('html2canvas')).default;

            let shareUrl = window.location.href;
            if (window.urlManager) {
                shareUrl = window.urlManager.getShareableURL();
            }

            const urlLength = shareUrl.length;
            let qrComplexity = 1;
            if (urlLength > 200) qrComplexity = 1.5;
            if (urlLength > 400) qrComplexity = 2.0;
            if (urlLength > 600) qrComplexity = 2.5;
            if (urlLength > 800) qrComplexity = 3.0;

            const pageWidthPx = width;
            const qrDisplaySizeRatio = 0.12 + (qrComplexity - 1) * 0.06;
            const qrDisplaySize = Math.round(pageWidthPx * qrDisplaySizeRatio);

            const minDisplaySize = Math.round((25 * dpi) / 25.4);
            const maxDisplaySize = Math.round((80 * dpi) / 25.4);
            const qrSize = Math.max(minDisplaySize, Math.min(qrDisplaySize, maxDisplaySize));

            const qrGenerationSize = Math.max(qrSize * 8, Math.round(2000 * qrComplexity));

            const footerHeightRatio = 0.05;
            const footerHeight = Math.round(height * footerHeightRatio);

            const footerPadding = Math.round(footerHeight * 0.15);
            const textGap = Math.round(footerHeight * 0.08);

            const titleFontSize = Math.round(footerHeight * 0.25);
            const descFontSize = Math.round(footerHeight * 0.18);
            const attrFontSize = Math.round(footerHeight * 0.15);
            const urlFontSize = Math.round(footerHeight * 0.13);

            const qrDataUrl = config.includeQRCode !== false
                ? await this._getQRCodeDataUrl(shareUrl, qrGenerationSize)
                : null;

            let attributionText = '';
            const attribCtrl = this._map._controls.find(c => c._container && c._container.classList.contains('mapboxgl-ctrl-attrib'));
            if (attribCtrl) {
                attributionText = attribCtrl._container.textContent;
            }

            const container = document.createElement('div');
            container.style.position = 'fixed';
            container.style.left = '-9999px';
            container.style.top = '0';
            container.style.width = width + 'px';
            container.style.height = height + 'px';
            container.style.background = '#ffffff';
            document.body.appendChild(container);

            const mapImg = document.createElement('img');
            mapImg.src = mapImageDataUrl;
            mapImg.style.width = '100%';
            mapImg.style.height = '100%';
            mapImg.style.display = 'block';
            container.appendChild(mapImg);

            await new Promise(resolve => {
                mapImg.onload = resolve;
                if (mapImg.complete) resolve();
            });

            const template = await this._loadFooterTemplate();
            if (!template) {
                console.warn('Could not load footer template, using plain map');
                document.body.removeChild(container);
                return mapImageDataUrl;
            }

            const head = document.createElement('head');
            const styleEl = template.styles.cloneNode(true);
            head.appendChild(styleEl);
            container.insertBefore(head, container.firstChild);

            const footerBox = template.footer.cloneNode(true);
            footerBox.style.cssText = 'position: absolute !important; bottom: 0 !important; left: 0 !important; right: 0 !important;';

            const qrEl = footerBox.querySelector('[data-export-qr]');
            if (qrEl && qrDataUrl) {
                qrEl.style.width = `${qrSize}px`;
                qrEl.style.height = `${qrSize}px`;
                qrEl.style.minWidth = `${qrSize}px`;
                qrEl.style.minHeight = `${qrSize}px`;
                qrEl.innerHTML = '';
                const qrImg = document.createElement('img');
                qrImg.src = qrDataUrl;
                qrImg.style.width = '100%';
                qrImg.style.height = '100%';
                qrImg.style.display = 'block';
                qrEl.appendChild(qrImg);
            }

            if (config.includeQRCode === false && qrEl) {
                qrEl.closest('.qr-box')?.remove();
            }

            const legendBox = footerBox.querySelector('.legend-box');
            if (config.includeLegend === false && legendBox) {
                legendBox.remove();
            }

            if (config.includeQRCode === false && config.includeLegend === false) {
                footerBox.querySelector('.metadata-right')?.remove();
            }

            const titleEl = footerBox.querySelector('[data-export-title]');
            if (titleEl) {
                if (this._title) {
                    titleEl.textContent = this._title.replace(/<br\s*\/?>/gi, ' ');
                } else {
                    titleEl.remove();
                }
            }

            const date = new Date();
            const timestamp = date.toLocaleString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });

            const attrEl = footerBox.querySelector('[data-export-attribution]');
            if (attrEl) {
                if (attributionText) {
                    attrEl.textContent = `Data: ${attributionText}`;
                } else {
                    attrEl.remove();
                }
            }

            const infoEl = footerBox.querySelector('[data-export-info]');
            if (infoEl) {
                const formattedUrl = this._formatUrlForDisplay(shareUrl);
                infoEl.textContent = `Exported ${timestamp} | ${formattedUrl}`;
            }

            const scaleEl = footerBox.querySelector('[data-export-scale-label]');
            if (scaleEl) {
                const center = this._map.getCenter();
                const zoom = this._map.getZoom();
                const metersPerPixel = 40075016.686 * Math.abs(Math.cos(center.lat * Math.PI / 180)) / Math.pow(2, zoom + 8);

                // Scale bar is 30mm wide in the template
                const scaleBarWidthMm = 30;
                const pixelsPerMm = dpi / 25.4;
                const scaleBarWidthPx = scaleBarWidthMm * pixelsPerMm;
                const scaleMeters = Math.round(metersPerPixel * scaleBarWidthPx);

                let scaleText;
                if (scaleMeters >= 1000) {
                    scaleText = `${(scaleMeters / 1000).toFixed(1)} km`;
                } else {
                    scaleText = `${scaleMeters} m`;
                }
                scaleEl.textContent = scaleText;
            }

            const northArrowEl = footerBox.querySelector('[data-export-north-arrow]');
            if (northArrowEl && bearing !== 0) {
                const container = northArrowEl.querySelector('.north-arrow-container');
                if (container) {
                    container.style.transform = `rotate(${-bearing}deg)`;
                }
            }

            const layerThumbnailsEl = footerBox.querySelector('[data-export-layer-thumbnails]');
            if (layerThumbnailsEl && window.stateManager && config.includeLegend !== false) {
                try {
                    console.log('[Export] Generating layer thumbnails...');

                    // Dynamic imports to avoid initialization issues
                    const { LayerThumbnail } = await import('./layer-thumbnail.js');
                    const { MapUtils } = await import('./map-utils.js');

                    const activeLayersMap = window.stateManager.getActiveLayers();
                    const bounds = this._map.getBounds();
                    const boundsArray = [
                        bounds.getWest(),
                        bounds.getSouth(),
                        bounds.getEast(),
                        bounds.getNorth()
                    ];

                    const imageToDataURL = async (url, useProxy = false) => {
                        return new Promise((resolve) => {
                            const img = new Image();
                            img.crossOrigin = 'anonymous';

                            const PROXY_URL = 'https://amche-atlas-production.up.railway.app/proxy';
                            const actualUrl = useProxy ? `${PROXY_URL}?url=${encodeURIComponent(url)}` : url;

                            const timeout = setTimeout(() => {
                                console.warn(`[Export] Image load timeout: ${actualUrl}`);
                                resolve(null);
                            }, 10000);

                            img.onload = () => {
                                clearTimeout(timeout);
                                try {
                                    const canvas = document.createElement('canvas');
                                    canvas.width = img.width;
                                    canvas.height = img.height;
                                    const ctx = canvas.getContext('2d');
                                    ctx.drawImage(img, 0, 0);
                                    const dataURL = canvas.toDataURL('image/png');
                                    console.log(`[Export] Successfully converted image${useProxy ? ' (via proxy)' : ''}: ${url.substring(0, 50)}...`);
                                    resolve(dataURL);
                                } catch (e) {
                                    console.warn(`[Export] Failed to convert image to canvas: ${actualUrl}`, e);
                                    resolve(null);
                                }
                            };

                            img.onerror = async (e) => {
                                clearTimeout(timeout);
                                if (!useProxy) {
                                    console.log(`[Export] Direct load failed, trying proxy server: ${url}`);
                                    const proxiedResult = await imageToDataURL(url, true);
                                    resolve(proxiedResult);
                                } else {
                                    console.warn(`[Export] Failed to load image even via proxy: ${url}`, e);
                                    resolve(null);
                                }
                            };

                            img.src = actualUrl;
                        });
                    };

                    let thumbnailCount = 0;
                    for (const [layerId, layerData] of activeLayersMap.entries()) {
                        try {
                            const config = { ...layerData.config };

                            if (window.layerRegistry) {
                                const registryLayer = window.layerRegistry.getLayer(config.id);
                                if (registryLayer && registryLayer.tags) {
                                    if (!config.tags) {
                                        config.tags = registryLayer.tags;
                                    } else if (Array.isArray(config.tags) && Array.isArray(registryLayer.tags)) {
                                        config.tags = [...new Set([...config.tags, ...registryLayer.tags])];
                                    }
                                }
                            }

                            if (config.headerImage && (config.headerImage.startsWith('http://') || config.headerImage.startsWith('https://'))) {
                                const dataURL = await imageToDataURL(config.headerImage);
                                if (dataURL) {
                                    config.headerImage = dataURL;
                                } else {
                                    console.warn(`[Export] Removing headerImage due to CORS/loading failure: ${config.headerImage}`);
                                    delete config.headerImage;
                                }
                            }

                            const row = document.createElement('div');
                            row.className = 'layer-thumbnail-row';

                            const thumbnail = LayerThumbnail.generate(config, 38, {
                                isInView: MapUtils.isLayerInView(config, boundsArray)
                            });
                            thumbnail.className = 'layer-thumbnail-item';
                            thumbnail.style.pointerEvents = 'none';
                            thumbnail.style.cursor = 'default';

                            // Remove any event listeners by cloning
                            const cleanThumbnail = thumbnail.cloneNode(true);
                            row.appendChild(cleanThumbnail);

                            const textContainer = document.createElement('div');
                            textContainer.className = 'layer-thumbnail-text';

                            const title = document.createElement('div');
                            title.className = 'layer-thumbnail-title';
                            title.textContent = config.title || config.id;
                            textContainer.appendChild(title);

                            if (config.attribution) {
                                const attribution = document.createElement('div');
                                attribution.className = 'layer-thumbnail-attribution';
                                attribution.innerHTML = config.attribution;
                                textContainer.appendChild(attribution);
                            }

                            row.appendChild(textContainer);

                            layerThumbnailsEl.appendChild(row);
                            thumbnailCount++;
                        } catch (thumbError) {
                            console.warn(`[Export] Failed to generate thumbnail for layer ${layerId}:`, thumbError);
                        }
                    }
                    console.log(`[Export] Generated ${thumbnailCount} layer thumbnails`);
                } catch (e) {
                    console.error('[Export] Failed to generate layer thumbnails:', e);
                }
            }

            container.appendChild(footerBox);

            await new Promise(resolve => requestAnimationFrame(resolve));
            await new Promise(resolve => requestAnimationFrame(resolve));

            const compositeCanvas = await html2canvas(container, {
                backgroundColor: '#ffffff',
                scale: 1,
                logging: false,
                useCORS: true,
                allowTaint: true,
                foreignObjectRendering: false,
                width: width,
                height: height,
                onclone: (clonedDoc) => {
                    const clonedContainer = clonedDoc.querySelector('div[style*="position: fixed"]');
                    if (clonedContainer) {
                        const clonedFooter = clonedContainer.querySelector('[id="export-footer-template"]');
                        if (clonedFooter) {
                            clonedFooter.style.display = 'flex';
                        }
                    }
                }
            });

            document.body.removeChild(container);

            return compositeCanvas.toDataURL('image/png');
        } catch (e) {
            console.warn('Failed to add footer to raster, using plain map', e);
            return mapImageDataUrl;
        }
    }


    _generateWorldFile(imageDimensions, transformer, center, pixelWidth, pixelHeight) {
        const pixelSizeX = imageDimensions.width / pixelWidth;
        const pixelSizeY = imageDimensions.height / pixelHeight;

        let centerX, centerY;

        if (transformer.coordSystem === 'wgs84') {
            centerX = center.lng;
            centerY = center.lat;
        } else if (transformer.coordSystem === 'local') {
            centerX = 0;
            centerY = 0;
        } else if (transformer.coordSystem === 'utm') {
            const transformedCenter = transformer._transformCoordinate([center.lng, center.lat]);
            centerX = transformedCenter[0];
            centerY = transformedCenter[1];
        } else {
            centerX = 0;
            centerY = 0;
        }

        const upperLeftX = centerX - (imageDimensions.width / 2);
        const upperLeftY = centerY + (imageDimensions.height / 2);

        let worldFile = '';
        worldFile += pixelSizeX + '\n';
        worldFile += '0.0\n';
        worldFile += '0.0\n';
        worldFile += (-pixelSizeY) + '\n';
        worldFile += upperLeftX + '\n';
        worldFile += upperLeftY + '\n';

        return worldFile;
    }

    /**
     * Style layer IDs for layers currently active (toggled on) in the layer
     * control, scoped the same way map-feature-control-iframe.js scopes
     * click/hover queries. Prevents "export all" from sweeping in basemap or
     * toggled-off layers via map.getStyle()/queryRenderedFeatures().
     */
    _getActiveStyleLayerIds() {
        return new Set(window.stateManager?.getInteractiveRenderedLayerIds() || []);
    }

    _hasSelectedFeatures() {
        if (!window.stateManager) {
            return false;
        }

        const selectedFeatures = this._getSelectedFeatures();
        return selectedFeatures.length > 0;
    }

    _getSelectedFeatures() {
        if (!window.stateManager) return [];

        const allLayers = window.stateManager.getActiveLayers();
        const selectedFeatures = [];

        allLayers.forEach((layerData, layerId) => {
            const { features } = layerData;
            if (features) {
                features.forEach((featureState, featureId) => {
                    if (featureState.isSelected) {
                        selectedFeatures.push({
                            feature: featureState.feature,
                            layerId: layerId,
                            layerConfig: layerData.config
                        });
                    }
                });
            }
        });

        return selectedFeatures;
    }

    _generateFilenameFromFeatures(selectedFeatures, extension) {
        const layerGroups = new Map();

        for (const item of selectedFeatures) {
            const layerId = item.layerId;
            if (!layerGroups.has(layerId)) {
                layerGroups.set(layerId, {
                    layerConfig: item.layerConfig,
                    features: []
                });
            }
            layerGroups.get(layerId).features.push(item.feature);
        }

        const parts = [];

        for (const [layerId, group] of layerGroups) {
            const layerTitle = group.layerConfig.title || layerId;
            const sanitizedLayer = layerTitle
                .replace(/[<>:"/\\|?*]/g, '')
                .replace(/\s+/g, '_');

            parts.push(sanitizedLayer);

            for (const feature of group.features) {
                const featureTitle = this._getFeatureTitle(feature, group.layerConfig);
                const sanitizedFeature = featureTitle
                    .replace(/[<>:"/\\|?*]/g, '')
                    .replace(/\s+/g, '_');
                parts.push(sanitizedFeature);
            }
        }

        const filename = parts.join('_').substring(0, 200);
        return `${filename}.${extension}`;
    }

    _getFeatureTitle(feature, layerConfig) {
        const labelField = layerConfig.inspect?.label;
        if (labelField && feature.properties[labelField]) {
            return String(feature.properties[labelField]);
        }

        if (feature.properties.name) {
            return String(feature.properties.name);
        }

        const firstPriorityField = layerConfig.inspect?.fields?.[0];
        if (firstPriorityField && feature.properties[firstPriorityField]) {
            return String(feature.properties[firstPriorityField]);
        }

        return 'Exported Feature';
    }

    _generateFilename(extension) {
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        const title = (this._title || 'map').replace(/[^a-z0-9]/gi, '_').substring(0, 50);
        return `${title}_${timestamp}.${extension}`;
    }

    _downloadFile(content, filename, mimeType) {
        const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
    }

    _getCurrentAtlasName() {
        try {
            if (window.layerRegistry && window.layerRegistry.currentAtlas) {
                return window.layerRegistry.currentAtlas.name || 'index';
            }
        } catch (e) {
            console.warn('Could not get atlas from layerRegistry:', e);
        }

        try {
            const params = new URLSearchParams(window.location.search);
            const atlasParam = params.get('atlas');
            if (atlasParam && !atlasParam.startsWith('http') && !atlasParam.startsWith('{')) {
                return atlasParam.replace('.atlas.json', '').replace('.json', '');
            }
        } catch (e) {
            console.warn('Could not parse atlas from URL:', e);
        }

        return 'index';
    }

    _getAtlasNameForLayer(layerId, layerConfig) {
        if (!layerId) return this._getCurrentAtlasName();

        try {
            if (window.layerRegistry && window.layerRegistry._atlases) {
                for (const [atlasName, atlasConfig] of window.layerRegistry._atlases.entries()) {
                    if (atlasConfig.layers && atlasConfig.layers.some(l => l.id === layerId)) {
                        console.log(`CSV Export: Found layer ${layerId} in atlas ${atlasName}`);
                        return atlasName;
                    }
                }
            }
        } catch (e) {
            console.warn('Could not find atlas for layer:', e);
        }

        const layerIdParts = layerId.split('-');
        if (layerIdParts.length > 0) {
            const potentialAtlas = layerIdParts[0];
            console.log(`CSV Export: Guessing atlas "${potentialAtlas}" from layer ID "${layerId}"`);
            return potentialAtlas;
        }

        return this._getCurrentAtlasName();
    }

    _getLayerConfigById(layerId) {
        if (!layerId) return null;

        if (window.stateManager) {
            const layers = window.stateManager.getActiveLayers();
            const layerData = layers.get(layerId);
            if (layerData?.config) {
                return layerData.config;
            }
        }

        return null;
    }

    async _extractHandlerData(htmlOutput, feature) {
        if (!htmlOutput) return '';

        const apiUrlMatch = htmlOutput.match(/const apiUrl = '([^']+)'/);
        if (apiUrlMatch && apiUrlMatch[1]) {
            const apiUrl = apiUrlMatch[1];
            console.log(`CSV Export: Found Bhunaksha API URL, fetching data...`);

            try {
                const response = await fetch(apiUrl);
                const data = await response.json();

                if (data.info && data.has_data === 'Y') {
                    const isHTML = /<[^>]*>/g.test(data.info);
                    let infoText;

                    if (isHTML) {
                        infoText = data.info
                            .replace(/<style>[\s\S]*?<\/style>/gi, '')
                            .replace(/<script[\s\S]*?<\/script>/gi, '')
                            .replace(/<[^>]*>/g, ' ')
                            .replace(/\s+/g, ' ')
                            .trim();
                    } else {
                        const rawText = data.info.split('\n').slice(3).join('\n').replace(/-{10,}/g, '');
                        infoText = rawText.replace(/^([^:\n]+:)/gm, '$1 ').replace(/\n/g, ' ');
                    }

                    console.log(`CSV Export: Fetched Bhunaksha data: "${infoText.substring(0, 100)}..."`);
                    return infoText.trim();
                } else {
                    return 'No occupant data available';
                }
            } catch (error) {
                console.warn('CSV Export: Failed to fetch Bhunaksha data:', error);
                return 'Error loading data';
            }
        }

        return this._stripHtmlTags(htmlOutput);
    }

    _stripHtmlTags(html) {
        if (!html) return '';
        return html
            .replace(/<style>[\s\S]*?<\/style>/gi, '')
            .replace(/<script[\s\S]*?<\/script>/gi, '')
            .replace(/<br\s*\/?>/gi, '\n')
            .replace(/<\/p>/gi, '\n')
            .replace(/<[^>]*>/g, '')
            .replace(/&nbsp;/g, ' ')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/\s+/g, ' ')
            .trim();
    }

    _isFeatureCompletelyInView(feature, bounds) {
        if (!feature || !feature.geometry) return false;

        const geometry = feature.geometry;
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();

        const isPointInBounds = (lng, lat) => {
            return lng >= sw.lng && lng <= ne.lng && lat >= sw.lat && lat <= ne.lat;
        };

        const checkCoordinates = (coords) => {
            if (typeof coords[0] === 'number') {
                return isPointInBounds(coords[0], coords[1]);
            }
            return coords.every(checkCoordinates);
        };

        switch (geometry.type) {
            case 'Point':
                return isPointInBounds(geometry.coordinates[0], geometry.coordinates[1]);

            case 'MultiPoint':
            case 'LineString':
                return geometry.coordinates.every(coord => isPointInBounds(coord[0], coord[1]));

            case 'MultiLineString':
            case 'Polygon':
                return geometry.coordinates.every(ring =>
                    ring.every(coord => isPointInBounds(coord[0], coord[1]))
                );

            case 'MultiPolygon':
                return geometry.coordinates.every(polygon =>
                    polygon.every(ring => ring.every(coord => isPointInBounds(coord[0], coord[1])))
                );

            default:
                return false;
        }
    }

    _parseExportURL() {
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const exportParam = urlParams.get('export');

            if (!exportParam) {
                return null;
            }

            const settings = JSON.parse(decodeURIComponent(exportParam));
            return settings;
        } catch (e) {
            console.warn('Failed to parse export URL parameter:', e);
            return null;
        }
    }
}
